import { DataPreview } from '@app/components/various/DataPreview';
import { Edit, Remove } from '@app/components/various/Icons';
import { addToast } from '@app/components/various/Toasts';
import { useDeleteShippingAddress } from '@app/services/hooks/account/useDeleteShippingAddress';
import { isDefined } from '@app/utils/is';
import { Addresses, IconComponentProps } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useConfirmationGuard } from '../useConfirmationGuard';

const RemoveIcon = (iconProps: IconComponentProps) => <Remove {...iconProps} className="text-error-45" />;

interface Props {
  accountId: string;
  shippingAddresses: Addresses.Shipping[];
  onEdit: (shippingAddressId: string) => void;
}

export const useGetShippingAddressOptions = ({ accountId, shippingAddresses, onEdit }: Props) => {
  const { t } = useTranslation(['common', 'shipping']);
  const confirmationGuard = useConfirmationGuard();
  const { mutate: deleteShippingAddress } = useDeleteShippingAddress();

  const handleDeleteClick = React.useCallback(
    (shippingAddressId: string) => {
      const shippingAddress = shippingAddresses.find(address => address.shippingAddress === shippingAddressId);

      if (!isDefined(shippingAddress)) {
        return;
      }

      if (shippingAddresses.length === 1) {
        addToast(t('shipping:last_address_error'), { type: 'error' });

        return;
      }

      confirmationGuard(() => ({
        content: (
          <div className="flex flex-col gap-6">
            {t('shipping:delete_warning')}
            <DataPreview
              /* check if address.company is string should be removed after fixing https://centracommerce.atlassian.net/browse/SBFT-3172 */
              title={typeof shippingAddress.company === 'string' ? shippingAddress.company : ''}
              data={shippingAddress}
              labels={[
                ['contactPerson', t('common:contact_person')],
                ['email', t('common:email')],
                ['address1', t('common:address')],
                ['city', t('common:city')],
                ['countryName', t('common:country')],
              ]}
            />
          </div>
        ),
        onOk: () => deleteShippingAddress({ accountId, shippingAddressId }),
        title: `${t('shipping:delete')}?`,
      }))();
    },
    [accountId, confirmationGuard, deleteShippingAddress, shippingAddresses, t],
  );

  return React.useCallback(
    (shippingAddressId: string) => [
      {
        icon: Edit,
        key: 'edit',
        label: t('common:edit'),
        onClick: () => onEdit(shippingAddressId),
      },
      {
        icon: RemoveIcon,
        key: 'delete',
        label: t('common:delete'),
        onClick: () => handleDeleteClick(shippingAddressId),
      },
    ],
    [handleDeleteClick, onEdit, t],
  );
};
